import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    steppableTypesOptions: Array,
    steppableId: Number,
    mayTakeDeadlineHash: Object,
    mayRequireApprovalHash: Object,
    mayHavePendingText: Object,
    mayHaveDescription: Object,
    mayHaveApplicationPendingText: Object,
    mayBeVisibleAt: Object,
    mayBeForMatchedOnly: Object,
    mayHaveDateRange: Object,
    orientationType: String,
    applicationsEnabled: Boolean
  }

  connect() {
    this.hideAll()
    this.setFormState()
    this.bindEvents()
  }

  bindEvents() {
    // Listen for changes on the steppable type select
    const steppableTypeSelect = document.getElementById('process_step_steppable_type')
    if (steppableTypeSelect) {
      steppableTypeSelect.addEventListener('change', () => {
        this.setFormState()
      })
    }

    // Listen for clicks on the require approval checkbox
    const requireApprovalCheckbox = document.getElementById('process_step_require_approval')
    if (requireApprovalCheckbox) {
      requireApprovalCheckbox.addEventListener('click', () => {
        this.setFormState()
      })
    }
  }

  hideAll() {
    // Hide all steppable type dropdowns (except orientation)
    this.steppableTypesOptionsValue.forEach(([steppableType, steppableName]) => {
      if (steppableType !== this.orientationTypeValue) {
        const selectId = this.toSnakeCase(steppableType) + '_id'
        this.setFormRowVisibilityBySelector(`#${selectId}`, false)
      }
    })

    // Hide other form elements
    this.setFormRowVisibilityBySelector('#process_step_require_approval', false)
    this.setFormRowVisibilityBySelector('#process_step_pending_text', false)
    this.setFormRowVisibilityBySelector('#process_step_description', false)
    this.setFormRowVisibilityBySelector('#process_step_application_pending_text', false)
    this.setFormRowVisibilityBySelector('[for="process_step_deadline"]', false)
    this.setFormRowVisibilityBySelector('[for="process_step_visibility_start"]', false)
    this.setFormRowVisibilityBySelector('#process_step_for_matched_only', false)
    this.setFormRowVisibilityBySelector('#auto_approve_if_approved_earlier', false)

    const matchingOptions = document.getElementById('matching-options')
    if (matchingOptions) matchingOptions.style.display = 'none'
  }

  toSnakeCase(text) {
    let newText = text.replace(/([A-Z])/g, ($1) => "_" + $1.toLowerCase())
    return newText.replace(/^_/, '')
  }

  requireApprovalChecked() {
    const checkbox = document.querySelector('#process_step_require_approval:checked')
    return checkbox !== null
  }

  setFormState() {
    const selectedOption = this.getSelectedSteppableType()
    if (!selectedOption) return

    const selectId = this.toSnakeCase(selectedOption.replace(/ /g, "_")) + '_id'

    this.hideAll()

    // Populate and show the correct dropdown
    const selectElement = document.getElementById(selectId)
    if (selectElement) {
      if (this.steppableIdValue) {
        selectElement.value = this.steppableIdValue
      }
      this.setFormRowVisibility(selectElement, true)
    }

    // Handle all the conditional form field visibility
    this.handleDeadlineLogic(selectedOption)
    this.handleApprovalLogic(selectedOption)
    this.handlePendingTextLogic(selectedOption)
    this.handleDescriptionLogic(selectedOption)
    this.handleApplicationPendingTextLogic(selectedOption)
    this.handleVisibilityLogic(selectedOption)
    this.handleMatchedOnlyLogic(selectedOption)
    this.handleDateRangeLogic(selectedOption)

    // Handle auto-approve checkboxes
    this.handleAutoApproveCheckboxes()
  }

  getSelectedSteppableType() {
    const select = document.getElementById('process_step_steppable_type')
    if (!select) {
      console.warn('Could not find #process_step_steppable_type select element')
      return null
    }

    const selectedOption = select.options[select.selectedIndex]
    return selectedOption ? selectedOption.value : null
  }

  handleDeadlineLogic(selectedType) {
    this.setFormRowVisibilityBySelector('[for="process_step_deadline"]', this.mayTakeDeadlineHashValue[selectedType])
  }

  handleApprovalLogic(selectedType) {
    this.setFormRowVisibilityBySelector('[for="process_step_require_approval"]', this.mayRequireApprovalHashValue[selectedType])
  }

  handlePendingTextLogic(selectedType) {
    const mayHave = this.mayHavePendingTextValue[selectedType]
    const element = '[for="process_step_pending_text"]'
    if (mayHave) {
      this.setFormRowVisibilityBySelector(element, this.requireApprovalChecked())
    } else {
      this.setFormRowVisibilityBySelector(element, false)
    }
  }

  handleDescriptionLogic(selectedType) {
    this.setFormRowVisibilityBySelector('[for="process_step_description"]', this.mayHaveDescriptionValue[selectedType])
  }

  handleApplicationPendingTextLogic(selectedType) {
    if (this.applicationsEnabledValue) {
      this.setFormRowVisibilityBySelector('[for="process_step_application_pending_text"]', this.mayHaveApplicationPendingTextValue[selectedType])
    }
  }

  handleVisibilityLogic(selectedType) {
    this.setFormRowVisibilityBySelector('[for="process_step_visibility_start"]', this.mayBeVisibleAtValue[selectedType])
  }

  handleMatchedOnlyLogic(selectedType) {
    const shouldShow = this.mayBeForMatchedOnlyValue[selectedType]

    this.setFormRowVisibilityBySelector('#process_step_for_matched_only', shouldShow)
    this.setFormRowVisibilityBySelector('#auto_approve_if_approved_earlier', shouldShow)
  }

  handleDateRangeLogic(selectedType) {
    const shouldShow = this.mayHaveDateRangeValue[selectedType]
    const matchingOptions = document.getElementById('matching-options')

    if (matchingOptions) {
      matchingOptions.style.display = shouldShow ? 'block' : 'none'
    }
  }

  handleAutoApproveCheckboxes() {
    const isChecked = this.requireApprovalChecked()

    const checkboxes = [
      '#process_step_auto_approve_if_approved_earlier',
      '#process_step_auto_approve_if_will_approve_later', 
      '#process_step_auto_approve_if_single_sign_on'
    ]

    const requireApprovalElement = document.querySelector('.require-approval')
    if (requireApprovalElement) {
      requireApprovalElement.classList.toggle('hidden', !isChecked)
    }

    checkboxes.forEach(selector => {
      document.querySelector(selector).disabled = !isChecked
    })
  }

  setFormRowVisibility(element, shouldShow) {
    if (!element) return
    element.closest('.form-row').style.display = shouldShow ? 'block' : 'none'
  }

  setFormRowVisibilityBySelector(selector, shouldShow) {
    this.setFormRowVisibility(document.querySelector(selector), shouldShow)
  }
}